import { debounce } from "lodash-es"
import { defineStore } from "pinia"
import type { Result } from "./results"
import { NoopSource, type Source } from "./sources"

type ComboBoxStoreDefinition = ReturnType<typeof defineComboBoxStore>
export type ComboBoxStore = ReturnType<ComboBoxStoreDefinition>

// Use a unique store for each ComboBox: https://github.com/vuejs/pinia/discussions/840
const definedStores = new Map<string, ComboBoxStoreDefinition>()

// Cache ComboBox stores by storeId
export const useComboBoxStore = (storeId: string): ComboBoxStore => {
  let definition = definedStores.get(storeId)

  if (definition === undefined) {
    definition = defineComboBoxStore(storeId)
    definedStores.set(storeId, definition)
  }

  return definition()
}

// Actually define the store for a given storeId
function defineComboBoxStore<Id extends string>(storeId: Id) {
  return defineStore(
    `combobox/${storeId}`,
    () => {
      const comboboxId = ref(storeId)
      const query = ref("")
      const field = ref("")
      const isLoading = ref(false)
      const isDropdownOpen = ref(false)
      const isSingleSelect = ref(false)
      const useInputValueByDefault = ref(false)
      const results = ref<Result[]>([])
      const selections = ref<Result[]>([])
      const source = ref<Source>(new NoopSource())

      const unselectedResults = computed(() => {
        return results.value.filter((result) => !isResultSelected(result))
      })

      const hasQuery = computed<boolean>(() => {
        return query.value.replace(/\s+/, "").length > 0
      })

      const search = debounce((showResultsOnEmptySearch: boolean): void => {
        if (query.value === "" && !showResultsOnEmptySearch) {
          closeDropdown()
          return
        }

        isDropdownOpen.value = true
        isLoading.value = true
        results.value = []

        source.value
          .fetch(query.value, field.value)
          .then((response) => {
            if (response === undefined) return
            for (const value of Object.values(response.data)) {
              results.value = results.value.concat(value as any[])
            }
          })
          .finally(() => {
            isLoading.value = false
          })
      }, 300)

      function selectResult(result: Result): void {
        const isExistingValue = selections.value.some(
          (val) => val.value === result.value,
        )

        if (!isExistingValue) {
          selections.value.push(result)
        }

        if (isSingleSelect.value) {
          query.value = result.text1
        } else {
          query.value = ""
        }

        closeDropdown()
      }

      function deselectResult(result: Result): void {
        selections.value = selections.value.filter(
          (r: Result) => r.id !== result.id,
        )
      }

      function isResultSelected(result: Result): boolean {
        return selections.value.some((r) => r.id === result.id)
      }

      function closeDropdown(): void {
        isDropdownOpen.value = false
      }

      function toggleDropdown(): void {
        isDropdownOpen.value = !isDropdownOpen.value
      }

      function textToResult(text: string): Result {
        return {
          id: text,
          value: text,
          text1: text,
        }
      }

      return {
        comboboxId,
        selections,
        unselectedResults,
        source,
        isLoading,
        isResultSelected,
        isDropdownOpen,
        isSingleSelect,
        useInputValueByDefault,
        hasQuery,
        query,
        field,
        closeDropdown,
        toggleDropdown,
        selectResult,
        deselectResult,
        search,
        textToResult,
      }
    },
  )
}
